import React from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { withBrandName } from '../../../config/appConfig';
import { TILE_TONES } from '../utils/reconConstants';
import CompactAmount from './CompactAmount';

// Design-reference stat tile: compact card, 11px uppercase label, tabular
// value, toned sub line (ok / warn / bad). Equal height across the row.
const StatTile = ({ label, value, sub, tone = 'default' }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxSizing: 'border-box',
    }}
  >
    <Typography
      sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}
      color="text.secondary"
    >
      {label}
    </Typography>
    <Box sx={{ mt: 0.5, minWidth: 0, overflow: 'hidden' }}>
      {typeof value === 'string' || value == null ? (
        <Typography sx={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {value ?? '—'}
        </Typography>
      ) : (
        value
      )}
    </Box>
    {sub && (
      <Box sx={{ mt: 0.5, minWidth: 0, overflow: 'hidden' }}>
        {typeof sub === 'string' ? (
          <Typography sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: TILE_TONES[tone] }}>
            {sub}
          </Typography>
        ) : (
          sub
        )}
      </Box>
    )}
  </Paper>
);

const signedCreditDeltaNode = (value, toneColor) => {
  const amount = Number(value ?? 0);
  const sign = amount < 0 ? '-' : amount > 0 ? '+' : '';
  const abs = Math.abs(amount);
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: toneColor, fontSize: 12 }}
    >
      <span>{sign}</span>
      <CompactAmount
        value={abs}
        prefix="AED "
        sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: toneColor }}
        iconSx={{ fontSize: 12 }}
      />
    </Box>
  );
};

// The reporting tab's four header tiles, fed by /api/v1/kpis `summary`.
const ReconKpiCards = ({ kpis, ledger = 'ar' }) => {
  const { reconciliation: r } = useTranslatedMessages();
  const t = r.tiles ?? {};

  const summary = kpis?.summary ?? {};
  const totalDocs = summary.total_documents;
  const gross = summary.gross_value;
  const vat = summary.vat_payable;
  const health = summary.health_percent;
  const isAp = ledger === 'ap';
  const creditCount = Number(summary.credit_note_count ?? 0).toLocaleString();
  const amountTone = (delta) => (Math.abs(delta ?? 0) > 0.01 ? 'warn' : 'ok');
  const healthTone = health > 80 ? 'ok' : health > 50 ? 'warn' : 'bad';

  const creditSub = (delta, label) => {
    const tone = amountTone(delta);
    return (
      <Typography
        component="div"
        sx={{
          fontSize: 12,
          fontVariantNumeric: 'tabular-nums',
          color: TILE_TONES[tone],
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
        }}
      >
        <span>{label}</span>
        <span>·</span>
        <span>{t.delta_word ?? 'Delta'}</span>
        {signedCreditDeltaNode(delta, TILE_TONES[tone])}
      </Typography>
    );
  };

  return (
    <Grid container spacing={1.5} alignItems="stretch">
      <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
        <StatTile
          label={t.total_documents ?? 'Total Documents'}
          value={totalDocs ? `${totalDocs.erp.toLocaleString()} / ${totalDocs.marmin.toLocaleString()}` : null}
          sub={withBrandName(t.total_documents_sub ?? 'ERP / {brandName}')}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
        <StatTile
          label={t.health ?? 'Reconciliation Health'}
          value={health === null || health === undefined ? null : `${Number(health).toFixed(2)}%`}
          sub={t.health_sub ?? 'Perfect matches / total'}
          tone={health === null || health === undefined ? 'default' : healthTone}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
        <StatTile
          label={isAp ? (t.gross_purchase ?? 'Gross Purchase Volume') : (t.gross_value ?? 'Gross Transaction Value')}
          value={
            gross ? (
              <CompactAmount
                value={gross.marmin}
                prefix="AED "
                sx={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
              />
            ) : null
          }
          sub={
            gross
              ? creditSub(gross.delta, `${t.credit_incl ?? 'Incl.'} ${creditCount} ${t.credit_notes ?? 'credit notes'}`)
              : null
          }
          tone={gross ? amountTone(gross.delta) : 'default'}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
        <StatTile
          label={isAp ? (t.vat_input ?? 'VAT Input Recoverable') : (t.vat_payable ?? 'VAT Output Payable')}
          value={
            vat ? (
              <CompactAmount
                value={vat.marmin}
                prefix="AED "
                sx={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
              />
            ) : null
          }
          sub={vat ? creditSub(vat.delta, t.credit_tax ?? 'Credit note tax') : null}
          tone={vat ? amountTone(vat.delta) : 'default'}
        />
      </Grid>
    </Grid>
  );
};

export default ReconKpiCards;
